import { useState, useEffect } from 'react';
import { useOutletContext, useParams, Link } from 'react-router-dom';
import { adminApi } from '../../services/adminApi';
import { ArrowLeft, CheckCircle2, Truck, Package, DollarSign, Mail, StickyNote, Loader2, User, GraduationCap, Calendar, MapPin } from 'lucide-react';
import { toast } from 'sonner';

export default function ReservationDetails() {
  const ctx = useOutletContext();
  const { id } = useParams();
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');

  useEffect(() => {
    adminApi.getReservation(id).then(d => { setRes(d); setLoading(false); }).catch(() => setLoading(false));
  }, [id]);

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-blue-400" /></div>;
  if (!res) return <div className="text-center py-12">Reservation not found</div>;

  const Section = ({ title, icon: Icon, children }) => (
    <div className={`${ctx.cardBg} rounded-xl border ${ctx.borderColor} p-5`}>
      <h3 className="text-sm font-semibold mb-4 flex items-center gap-2">{Icon && <Icon className="w-4 h-4 text-blue-400" />}{title}</h3>
      {children}
    </div>
  );

  const Field = ({ label, value }) => (
    <div>
      <p className={`text-xs ${ctx.textSecondary} mb-0.5`}>{label}</p>
      <p className="text-sm font-medium">{value || '—'}</p>
    </div>
  );

  const StatusBadge = ({ status, paid }) => {
    const color = paid || status === 'Completed' ? 'emerald' : status === 'Scheduled' ? 'blue' : status === 'In Progress' ? 'amber' : 'gray';
    return <span className={`text-xs px-2.5 py-1 rounded-full bg-${color}-500/10 text-${color}-400`}>{paid !== undefined ? (paid ? 'Paid' : 'Pending') : status}</span>;
  };

  return (
    <div data-testid="admin-reservation-detail">
      <div className="flex items-center gap-3 mb-6">
        <Link to="/admin/reservations" className={`p-2 rounded-lg ${ctx.hoverBg} ${ctx.textSecondary}`}><ArrowLeft className="w-4 h-4" /></Link>
        <div>
          <h1 className="text-2xl font-bold" style={{ fontFamily: 'Chivo, sans-serif' }}>{res.reservation_number}</h1>
          <p className={`text-sm ${ctx.textSecondary}`}>{res.student_name} &middot; {res.school}</p>
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap gap-2 mb-6">
        {!res.deposit_paid && (
          <button onClick={() => { adminApi.markDepositPaid(res._id); setRes(p => ({...p, deposit_paid: true})); toast.success('Deposit marked paid'); }}
            className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 text-white rounded-lg text-xs font-medium hover:bg-emerald-700">
            <CheckCircle2 className="w-3.5 h-3.5" /> Mark Deposit Paid
          </button>
        )}
        <button className="flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700">
          <Truck className="w-3.5 h-3.5" /> Update Pickup
        </button>
        <button className="flex items-center gap-1.5 px-3 py-2 bg-purple-600 text-white rounded-lg text-xs font-medium hover:bg-purple-700">
          <Package className="w-3.5 h-3.5" /> Update Delivery
        </button>
        <button className="flex items-center gap-1.5 px-3 py-2 bg-cyan-600 text-white rounded-lg text-xs font-medium hover:bg-cyan-700">
          <Mail className="w-3.5 h-3.5" /> Send Email
        </button>
      </div>

      <div className="grid lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <Section title="Student Information" icon={User}>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Full Name" value={res.student_name} />
              <Field label="Email" value={res.email} />
              <Field label="Phone" value={res.phone} />
              <Field label="How Heard" value={res.how_heard} />
            </div>
          </Section>

          <Section title="School & Location" icon={GraduationCap}>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="University" value={res.school} />
              <Field label="Residence Hall" value={res.residence_hall} />
            </div>
          </Section>

          <Section title="Pickup Information" icon={Truck}>
            <div className="grid sm:grid-cols-3 gap-4">
              <Field label="Pickup Date" value={res.pickup_date} />
              <Field label="Status" value={<StatusBadge status={res.pickup_status} />} />
              <Field label="Items" value={res.item_count} />
            </div>
          </Section>

          <Section title="Delivery Information" icon={MapPin}>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Delivery Date" value={res.delivery_date} />
              <Field label="Status" value={<StatusBadge status={res.delivery_status} />} />
            </div>
          </Section>

          {/* Internal Notes */}
          <Section title="Internal Notes" icon={StickyNote}>
            {res.notes && (
              <div className={`p-3 rounded-lg border ${ctx.borderColor} text-xs whitespace-pre-wrap mb-3 ${ctx.textSecondary}`}>
                {res.notes}
              </div>
            )}
            <textarea value={note} onChange={e => setNote(e.target.value)} rows={3}
              data-testid="internal-note-input"
              placeholder="Add internal note..."
              className={`w-full p-3 rounded-lg text-sm border ${ctx.borderColor} bg-transparent outline-none focus:ring-2 focus:ring-blue-500/30 resize-none`} />
            <button onClick={async () => {
                if (!note.trim()) return;
                try {
                  const r = await adminApi.addInternalNote(res._id, note);
                  setRes(prev => ({ ...prev, notes: (prev.notes ? prev.notes + '\n' : '') + r.note }));
                  setNote('');
                  toast.success('Note saved');
                } catch {
                  toast.error('Failed to save note');
                }
              }}
              data-testid="save-note-btn"
              className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg text-xs font-medium hover:bg-blue-700">Save Note</button>
          </Section>
        </div>

        {/* Right sidebar */}
        <div className="space-y-4">
          <Section title="Payment Summary" icon={DollarSign}>
            <div className="space-y-3">
              <div className="flex justify-between"><span className={`text-xs ${ctx.textSecondary}`}>Deposit</span><StatusBadge paid={res.deposit_paid} /></div>
              <div className="flex justify-between"><span className={`text-xs ${ctx.textSecondary}`}>Deposit Amount</span><span className="text-sm font-medium">$30</span></div>
              <div className="flex justify-between"><span className={`text-xs ${ctx.textSecondary}`}>Balance Due</span><span className="text-sm font-bold text-amber-400">${res.balance_due}</span></div>
              <div className={`border-t ${ctx.borderColor} pt-3 flex justify-between`}>
                <span className={`text-xs ${ctx.textSecondary}`}>Total Items</span><span className="text-sm font-medium">{res.item_count}</span>
              </div>
            </div>
          </Section>

          <Section title="Activity Timeline" icon={Calendar}>
            <div className="space-y-3">
              <div className="flex gap-3">
                <div className="w-2 h-2 rounded-full bg-blue-400 mt-1.5 shrink-0" />
                <div><p className="text-xs font-medium">Reservation created</p><p className={`text-xs ${ctx.textSecondary}`}>{new Date(res.created_date).toLocaleDateString()}</p></div>
              </div>
              {res.deposit_paid && (
                <div className="flex gap-3">
                  <div className="w-2 h-2 rounded-full bg-emerald-400 mt-1.5 shrink-0" />
                  <div><p className="text-xs font-medium">Deposit paid</p><p className={`text-xs ${ctx.textSecondary}`}>$30 via Stripe</p></div>
                </div>
              )}
              <div className="flex gap-3">
                <div className="w-2 h-2 rounded-full bg-gray-500 mt-1.5 shrink-0" />
                <div><p className="text-xs font-medium">Pickup {res.pickup_status.toLowerCase()}</p><p className={`text-xs ${ctx.textSecondary}`}>{res.pickup_date}</p></div>
              </div>
            </div>
          </Section>
        </div>
      </div>
    </div>
  );
}
